import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetAdminCalendarQueryKey,
  useCreateAdminBooking,
  useGetAdminCalendar,
  useGetAdminQuote,
} from '../../../api/generated/endpoints/admin/admin'
import { useListRates } from '../../../api/generated/endpoints/rates/rates'
import type { CoachRead, RateRead } from '../../../api/generated/model'
import { StudentPicker } from '../../../components/StudentPicker'
import type { Student } from '../../../components/StudentPicker'
import { apiMessage } from '../../../lib/api-error'
import { addMinutes, isLocalDate, isStartTime } from '../../../lib/slots'
import { toClock, toMinutes } from '../../../lib/admin-calendar'
import { formatUYU } from '../../../lib/money'
import { formatDayLong, todayAtFacility } from '../../../lib/time'

/**
 * Putting something in a slot: a class, a block, or a booking for somebody.
 *
 * A full screen rather than a modal or an inline expansion. A modal brings a
 * focus trap, an escape key, and -- fatally -- dismissal by tapping outside: a
 * resting palm loses the work with no explanation. Inline would reflow a
 * two-column grid and shove the thing being looked at off screen. A route gives
 * back, reload and a shareable URL for free, and puts exactly one thing on
 * screen.
 *
 * `kind` lives in the URL so Back steps from the form to the chooser naturally.
 */
export const Route = createFileRoute('/_authed/_admin/admin/new')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { date: string; bay: number; start: string; kind?: string } => {
    const bay = Number(search.bay)
    return {
      date: isLocalDate(search.date) ? search.date : todayAtFacility(),
      bay: Number.isInteger(bay) && bay > 0 ? bay : 1,
      start: isStartTime(search.start) ? search.start : '08:00',
      kind: ['class', 'block', 'practice'].includes(String(search.kind))
        ? String(search.kind)
        : undefined,
    }
  },
  component: NewBookingPage,
})

/**
 * Why a bay is being taken out of service.
 *
 * The value is STORED, in admin_note, and read back on the calendar and the
 * booking screen -- so it has to be Spanish too, not just the label. It was
 * `['Class', 'Maintenance', …]` used as both, which meant the coach picked an
 * English word and then found it written on his own schedule.
 *
 * Neither scanner could see it: copy.test.ts reads JSX text, attributes and
 * object-literal values, and a bare array of strings is none of those. The
 * route was outside the browser sweep as well, so nothing had ever rendered it.
 */
const BLOCK_REASONS = ['Clase', 'Mantenimiento', 'Evento privado', 'Otro']
const COACH_KEY = 'golf-bay-manager:last-coach'

function NewBookingPage() {
  const search = Route.useSearch()
  const calendar = useGetAdminCalendar(
    { from: search.date, to: search.date },
    { query: { staleTime: 30_000 } },
  )

  const bayName =
    calendar.data?.bays.find((bay) => bay.id === search.bay)?.name ?? `Simulador ${search.bay}`
  const slotMinutes = calendar.data?.slot_minutes ?? 60
  const closesAt = calendar.data?.closes_at ?? '21:00'
  const coaches = calendar.data?.coaches ?? []

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <Link
        to="/admin/calendar"
        search={{ date: search.date }}
        className="inline-flex min-h-14 items-center text-lg underline"
      >
        &lsaquo;&nbsp; Volver a {formatDayLong(search.date)}
      </Link>

      {/* Restated on every screen, so a wrong day is caught before committing. */}
      <div className="rounded-lg bg-slate-900 px-6 py-5 text-white">
        <p className="text-xl font-semibold">
          {bayName} &middot; {formatDayLong(search.date)}
        </p>
        {/* The END only before a form is open: once a line and a length are
            chosen, the guess from slot_minutes is wrong for anything but one
            hour, and the price box below states the real one. */}
        <p className="text-2xl tabular-nums">
          {search.start}
          {!search.kind && <> &rarr; {addMinutes(search.start, slotMinutes)}</>}
        </p>
      </div>

      {!search.kind && <Chooser />}
      {search.kind === 'block' && <BlockForm closesAt={closesAt} />}
      {search.kind === 'class' && <ClassForm coaches={coaches} />}
      {search.kind === 'practice' && <ManualForm />}
    </div>
  )
}

function Chooser() {
  const search = Route.useSearch()

  // All three make the bay unavailable. Choosing the "wrong" one is a labelling
  // mistake, not a correctness one -- which is exactly why a three-way choice
  // is safe to put in front of somebody who should not have to think hard.
  const options = [
    { kind: 'class', title: 'Clase', blurb: 'Francisco o Sergio dando una clase' },
    {
      kind: 'block',
      title: 'Bloqueo',
      blurb: 'Dejar el simulador libre — mantenimiento, evento privado',
    },
    { kind: 'practice', title: 'Reserva', blurb: 'Alguien llamó y quiere el simulador' },
  ] as const

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">¿Qué va acá?</h1>
      {options.map((option) => (
        <Link
          key={option.kind}
          to="/admin/new"
          search={{ ...search, kind: option.kind }}
          className="flex min-h-24 w-full flex-col justify-center rounded-lg border-2 border-slate-400 bg-white px-6 hover:bg-slate-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          <span className="text-2xl font-semibold">{option.title}</span>
          <span className="text-base text-slate-700">{option.blurb}</span>
        </Link>
      ))}
    </div>
  )
}

/** Shared submit plumbing: every form ends the same way. */
function useCreate() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useCreateAdminBooking({
    mutation: {
      retry: false,
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getGetAdminCalendarQueryKey() })
        void navigate({ to: '/admin/calendar', search: { date: search.date } })
      },
    },
  })
}

function SubmitError({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <div role="alert" className="rounded-lg border-2 border-red-300 bg-red-50 p-5">
      <p className="text-xl text-red-900">
        {apiMessage(error, 'No se pudo guardar. Se puede volver a intentar.')}
      </p>
    </div>
  )
}

/** Chip group. Same visually-hidden radio idiom as the customer time picker:
 *  arrow keys, one tab stop, correct aria-checked, no keyboard handler. */
function Chips({
  legend,
  hint,
  options,
  value,
  onChange,
  name,
}: {
  legend: string
  hint?: string
  options: { value: string; label: string }[]
  value: string
  onChange: (next: string) => void
  name: string
}) {
  return (
    <fieldset>
      <legend className="text-xl font-semibold">{legend}</legend>
      {hint && <p className="mt-1 text-base text-slate-700">{hint}</p>}
      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="peer sr-only"
            />
            <span className="flex min-h-18 cursor-pointer items-center justify-center rounded-lg border-2 border-slate-400 bg-white px-3 text-center text-lg peer-not-checked:hover:bg-slate-50 peer-checked:border-slate-900 peer-checked:bg-slate-900 peer-checked:text-white peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-slate-900">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function BlockForm({ closesAt }: { closesAt: string }) {
  const search = Route.useSearch()
  const create = useCreate()
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [hours, setHours] = useState('1')

  const restOfDay = Math.max(1, Math.floor((toMinutes(closesAt) - toMinutes(search.start)) / 60))

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Bloquear el simulador</h1>

      {/* Preset reasons turn the commonest task into zero typing. */}
      <Chips
        legend="¿Por qué?"
        name="reason"
        value={reason}
        onChange={setReason}
        options={BLOCK_REASONS.map((r) => ({ value: r, label: r }))}
      />

      <Chips
        legend="¿Cuánto dura?"
        name="hours"
        value={hours}
        onChange={setHours}
        options={[
          { value: '1', label: '1 hora' },
          { value: '2', label: '2 horas' },
          { value: '3', label: '3 horas' },
          { value: String(restOfDay), label: 'Resto del día' },
        ]}
      />

      <div>
        <label htmlFor="note" className="block text-xl font-semibold">
          ¿Algo para recordar? <span className="font-normal text-slate-700">(opcional)</span>
        </label>
        <input
          id="note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="mt-2 min-h-16 w-full rounded-md border-2 border-slate-400 px-4 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        />
      </div>

      <SubmitError error={create.error} />

      <button
        type="button"
        disabled={create.isPending}
        onClick={() =>
          create.mutate({
            data: {
              kind: 'block',
              bay_id: search.bay,
              date: search.date,
              start: search.start,
              hours: Number(hours),
              admin_note: [reason, note].filter(Boolean).join(' — ') || null,
            },
          })
        }
        className="min-h-20 w-full rounded-lg bg-slate-900 px-6 text-2xl font-semibold text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
      >
        {create.isPending ? 'Bloqueando…' : 'Bloquear el simulador'}
      </button>
    </div>
  )
}

/**
 * What is being sold: how many people, under which line of the price list, for
 * how long -- and what that comes to. Decision 0005.
 *
 * The price is ASKED FOR, never multiplied here. This form used to carry
 * `const hourPrice = 600`, which was right until the day it was not; the
 * quote endpoint is the same arithmetic the booking will be charged with.
 *
 * People come first because they decide the line: "1 persona" and "2 a 4" are
 * different lines, and asking the coach to pick a tariff by name would put the
 * price list's vocabulary in front of somebody who thinks in "how many".
 */
type Sale = { rateId: number; headcount: number; hours: number }

function useSale(withCoach: boolean) {
  const search = Route.useSearch()
  const rates = useListRates({ query: { staleTime: 60_000 } })
  const lines: RateRead[] = (rates.data?.rates ?? []).filter(
    (rate) => rate.with_coach === withCoach && rate.prices.some((p) => p.bay_id === search.bay),
  )
  const mostPeople = Math.max(1, ...lines.map((rate) => rate.max_people))

  const [headcount, setHeadcount] = useState(1)
  const fitting = lines.filter((r) => r.min_people <= headcount && headcount <= r.max_people)
  const [chosenId, setChosenId] = useState<number | null>(null)
  // A choice that no longer fits the headcount falls back to the first that
  // does, rather than sending a line the server will refuse.
  const rate = fitting.find((r) => r.id === chosenId) ?? fitting[0] ?? null
  const [hours, setHours] = useState(1)

  const sale: Sale | null = rate ? { rateId: rate.id, headcount, hours } : null
  const quote = useGetAdminQuote(
    {
      bay_id: search.bay,
      rate_id: sale?.rateId ?? 0,
      headcount: sale?.headcount ?? 1,
      hours: sale?.hours ?? 1,
    },
    { query: { enabled: sale !== null, retry: false } },
  )

  const picker = (
    <div className="space-y-8">
      {mostPeople > 1 && (
        <Chips
          legend="¿Cuántas personas?"
          name="headcount"
          value={String(headcount)}
          onChange={(next) => setHeadcount(Number(next))}
          options={Array.from({ length: mostPeople }, (_, i) => ({
            value: String(i + 1),
            label: String(i + 1),
          }))}
        />
      )}

      {fitting.length > 1 && (
        <Chips
          legend="¿Qué se juega?"
          name="rate"
          value={String(rate?.id ?? '')}
          onChange={(next) => setChosenId(Number(next))}
          options={fitting.map((r) => ({ value: String(r.id), label: r.name }))}
        />
      )}
      {fitting.length === 1 && rate && <p className="text-xl font-semibold">{rate.name}</p>}

      {rate?.pricing === 'hourly' && (
        <Chips
          legend="¿Cuánto dura?"
          name="hours"
          value={String(hours)}
          onChange={(next) => setHours(Number(next))}
          options={[1, 2, 3, 4].map((h) => ({
            value: String(h),
            label: h === 1 ? '1 hora' : `${h} horas`,
          }))}
        />
      )}
      {rate?.pricing === 'round' && rate.round_minutes !== null && (
        <p className="text-lg text-slate-700">
          Ocupa el simulador{' '}
          {rate.round_minutes / 60 === 1 ? '1 hora' : `${rate.round_minutes / 60} horas`}.
        </p>
      )}

      {!rates.isPending && lines.length === 0 && (
        <p className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5 text-xl">
          {withCoach
            ? 'Este simulador no tiene ninguna tarifa con profesor. Se agrega en Ajustes.'
            : 'Este simulador no tiene ninguna tarifa. Se agrega en Ajustes.'}
        </p>
      )}
      {lines.length > 0 && !rate && (
        <p className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5 text-xl">
          Ninguna tarifa de este simulador es para {headcount}{' '}
          {headcount === 1 ? 'persona' : 'personas'}.
        </p>
      )}

      {quote.data && (
        <div className="rounded-lg border-2 border-slate-300 bg-white p-5 text-xl">
          <p className="tabular-nums">
            De {search.start} a {addMinutes(search.start, quote.data.minutes)}.
          </p>
          <p>
            Debe <span className="font-semibold">{formatUYU(quote.data.price_uyu_pesos)}</span> en
            el mostrador.
          </p>
          {quote.data.coach_share_uyu_pesos > 0 && (
            <p className="mt-1 text-base text-slate-700">
              De eso, {formatUYU(quote.data.coach_share_uyu_pesos)} son del profesor y{' '}
              {formatUYU(quote.data.facility_share_uyu_pesos)} del local.
            </p>
          )}
        </div>
      )}
      {/* isError, not `error &&`: Orval types this hook's error as void, so a
          truthiness test on it does not narrow. The 422 detail is Spanish. */}
      {quote.isError && (
        <p role="alert" className="text-xl text-red-900">
          {apiMessage(quote.error as unknown, 'No se pudo calcular el precio.')}
        </p>
      )}
    </div>
  )

  return { sale: quote.data ? sale : null, picker }
}

function ClassForm({ coaches }: { coaches: CoachRead[] }) {
  const search = Route.useSearch()
  const create = useCreate()
  const [student, setStudent] = useState<Student | null>(null)
  // Remembered, because he books for himself most of the time -- usually zero
  // taps. Stored as the coach's id; a remembered coach who has since been
  // retired falls back to the first on the list.
  //
  // DERIVED, not a useState initialiser: the coaches arrive with the calendar,
  // after the first render, and an initialiser that ran against an empty list
  // left no coach chosen and the save button dead -- seen in the browser, not
  // caught by the types.
  const [picked, setPicked] = useState<number | null>(null)
  const remembered = Number(localStorage.getItem(COACH_KEY))
  const coachId =
    [picked, remembered].find((id) => coaches.some((c) => c.id === id)) ?? coaches[0]?.id ?? null
  const setCoachId = setPicked
  const { sale, picker } = useSale(true)

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Agregar una clase</h1>

      <StudentPicker label="¿Para quién es la clase?" value={student} onChange={setStudent} />

      {coaches.length > 0 && (
        <Chips
          legend="¿Quién da la clase?"
          name="coach"
          value={String(coachId ?? '')}
          onChange={(next) => {
            setCoachId(Number(next))
            localStorage.setItem(COACH_KEY, next)
          }}
          options={coaches.map((coach) => ({ value: String(coach.id), label: coach.name }))}
        />
      )}

      {picker}

      <SubmitError error={create.error} />

      <div>
        <button
          type="button"
          disabled={create.isPending || !student || !sale || coachId === null}
          onClick={() =>
            student &&
            sale &&
            create.mutate({
              data: {
                kind: 'class',
                bay_id: search.bay,
                date: search.date,
                start: search.start,
                hours: sale.hours,
                rate_id: sale.rateId,
                headcount: sale.headcount,
                coach_id: coachId,
                ...(student.mode === 'existing'
                  ? { student_id: student.customer.id }
                  : { student_name: student.name, student_phone: student.phone || null }),
              },
            })
          }
          className="min-h-20 w-full rounded-lg bg-slate-900 px-6 text-2xl font-semibold text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-200 disabled:text-slate-500"
        >
          {create.isPending ? 'Guardando…' : 'Guardar la clase'}
        </button>
        {/* A word, not just a greyed button. */}
        {!student && (
          <p className="mt-2 text-base text-slate-700">Primero hay que elegir un alumno.</p>
        )}
      </div>
    </div>
  )
}

function ManualForm() {
  const search = Route.useSearch()
  const create = useCreate()
  const [student, setStudent] = useState<Student | null>(null)
  const { sale, picker } = useSale(false)

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Reservar el simulador para alguien</h1>

      <StudentPicker label="¿Para quién es?" value={student} onChange={setStudent} />

      {picker}

      <SubmitError error={create.error} />

      <div>
        <button
          type="button"
          disabled={create.isPending || !student || !sale}
          onClick={() =>
            student &&
            sale &&
            create.mutate({
              data: {
                kind: 'practice',
                bay_id: search.bay,
                date: search.date,
                start: search.start,
                hours: sale.hours,
                rate_id: sale.rateId,
                headcount: sale.headcount,
                ...(student.mode === 'existing'
                  ? { student_id: student.customer.id }
                  : { student_name: student.name, student_phone: student.phone || null }),
              },
            })
          }
          className="min-h-20 w-full rounded-lg bg-slate-900 px-6 text-2xl font-semibold text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-200 disabled:text-slate-500"
        >
          {create.isPending ? 'Guardando…' : 'Guardar la reserva'}
        </button>
        {!student && (
          <p className="mt-2 text-base text-slate-700">Primero hay que elegir para quién es.</p>
        )}
      </div>
    </div>
  )
}

export { toClock }
